<script lang="ts" module>
  /** A row of the ban or mute list. */
  export type RestrictedUser = {
    userId: string;
    name: string | null;
    profiles: { userId: string; name: string | null }[];
    /**
     * null only for a ban from before bans lasted at most 90 days, until the
     * retention job has given it an end
     */
    expiresAt: number | null;
    /** null when it was issued before the details were recorded */
    issuedAt: number | null;
    issuedBy?: string;
    reason?: string;
  };
</script>

<script lang="ts">
  import { format } from 'date-fns';
  import { nb } from 'date-fns/locale';
  import { onMount } from 'svelte';
  import { SvelteSet } from 'svelte/reactivity';
  import { adminName } from '../../stores/configStore';
  import { addToast } from '../../stores/toastStore';
  import AccountMessages from './AccountMessages.svelte';

  interface Props {
    users: RestrictedUser[] | null;
    /** `{ noun: 'ban', done: 'banned', undo: 'unban', undone: 'unbanned' }` */
    words: { noun: string; done: string; undo: string; undone: string };
    /** Lifts the ban or mute of every id, and refreshes `users`. */
    lift: (userIds: string[], issuedBy: string) => Promise<void>;
  }
  let { users, words, lift }: Props = $props();

  const capitalize = (word: string) =>
    word.charAt(0).toUpperCase() + word.slice(1);

  let filter = $state('');
  let confirming = $state<string | null>(null);
  let lifting = $state<string | null>(null);
  let now = $state(Date.now());
  const showingMessages = new SvelteSet<string>();

  const toggleMessages = (userId: string) => {
    if (!showingMessages.delete(userId)) {
      showingMessages.add(userId);
    }
  };

  const filtered = $derived.by(() => {
    const query = filter.trim().toLowerCase();
    if (users == null || query === '') {
      return users;
    }
    return users.filter(
      (user) =>
        user.name?.toLowerCase().includes(query) ||
        user.userId.includes(query) ||
        user.profiles.some(
          (profile) =>
            profile.name?.toLowerCase().includes(query) ||
            profile.userId.includes(query)
        )
    );
  });

  const label = (user: RestrictedUser) =>
    user.name ??
    (user.profiles.length > 0
      ? `parent account of ${user.profiles.map((p) => p.name ?? p.userId).join(', ')}`
      : user.userId);

  const formatRemaining = (expiresAt: number) => {
    const remaining = Math.max(0, expiresAt - now);
    const days = Math.floor(remaining / 86_400_000);
    const hours = Math.floor(remaining / 3_600_000) % 24;
    const minutes = Math.floor(remaining / 60_000) % 60;
    if (days > 0) {
      return `${days}d ${hours}h`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m`;
    }
    return '< 1m';
  };

  const handleLift = async (user: RestrictedUser) => {
    confirming = null;
    lifting = user.userId;
    try {
      await lift(
        [user.userId, ...user.profiles.map((profile) => profile.userId)],
        $adminName
      );
      addToast({ message: `'${label(user)}' was ${words.undone}` });
    } catch (e) {
      addToast({
        message: `Couldn't ${words.undo} '${label(user)}': ${String(e)}`,
        type: 'error',
      });
    } finally {
      lifting = null;
    }
  };

  onMount(() => {
    const tick = setInterval(() => (now = Date.now()), 30_000);
    return () => clearInterval(tick);
  });
</script>

<div class="container">
  {#if users == null || filtered == null}
    <p class="empty-state">Loading {words.noun}s...</p>
  {:else}
    <div class="header">
      <span class="count">
        {users.length}
        {users.length === 1 ? words.noun : `${words.noun}s`}
      </span>
      <input
        type="search"
        bind:value={filter}
        placeholder="Filter by name or id"
        aria-label="Filter {words.noun}s"
      />
    </div>
    <div class="items">
      {#each filtered as user (user.userId)}
        <div class="item">
          <div class="item-content">
            <div class="title">
              {#if user.name != null}
                <strong>{user.name}</strong>
              {:else if user.profiles.length > 0}
                <strong class="unknown">Parent account</strong>
              {:else}
                <strong class="unknown">Name unknown</strong>
              {/if}
              {#if user.expiresAt != null}
                <span
                  class="tag"
                  title={format(user.expiresAt, 'dd.MM.yyyy HH:mm', {
                    locale: nb,
                  })}
                >
                  {formatRemaining(user.expiresAt)} left
                </span>
              {/if}
            </div>
            <span class="id">{user.userId}</span>
            {#each user.profiles as profile (profile.userId)}
              <span class="profile">
                Profile {profile.name ?? 'name unknown'}
                <span class="id">{profile.userId}</span>
              </span>
            {/each}
            <span class="details">
              {#if user.issuedAt != null}
                {capitalize(words.done)}
                {format(user.issuedAt, 'dd.MM.yyyy HH:mm', {
                  locale: nb,
                })}
                {#if user.issuedBy != null}by {user.issuedBy}{/if}
                {#if user.reason != null && user.reason !== ''}
                  · {user.reason}
                {/if}
              {:else}
                {capitalize(words.done)} before {words.noun} details were recorded
              {/if}
            </span>
            <button
              class="toggle"
              aria-expanded={showingMessages.has(user.userId)}
              onclick={() => toggleMessages(user.userId)}
            >
              {showingMessages.has(user.userId)
                ? 'Hide messages'
                : 'Show messages'}
            </button>
          </div>
          <div class="actions">
            {#if confirming === user.userId}
              <button class="btn danger" onclick={() => handleLift(user)}>
                Confirm
              </button>
              <button class="btn" onclick={() => (confirming = null)}>
                Cancel
              </button>
            {:else}
              <button
                class="btn"
                disabled={lifting === user.userId}
                aria-busy={lifting === user.userId}
                onclick={() => (confirming = user.userId)}
              >
                {capitalize(words.undo)}
              </button>
            {/if}
          </div>
          {#if showingMessages.has(user.userId)}
            <div class="messages">
              <AccountMessages
                userIds={[
                  user.userId,
                  ...user.profiles.map((profile) => profile.userId),
                ]}
              />
            </div>
          {/if}
        </div>
      {/each}
      {#if users.length === 0}
        <p class="empty-state">No {words.done} users</p>
      {:else if filtered.length === 0}
        <p class="empty-state">No {words.noun}s match '{filter}'</p>
      {/if}
    </div>
  {/if}
</div>

<style>
  .container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    font-size: 0.95rem;
    margin-top: 0.75rem;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .count {
    color: var(--color-zinc-300);
  }

  .items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--color-zinc-800);
    border-radius: 0.5rem;
    padding: 0.75rem;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .messages {
    flex-basis: 100%;
  }

  .item-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
    min-width: 0;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .title strong {
    font-weight: 600;
    word-break: break-word;
  }

  .unknown {
    color: var(--color-zinc-400);
    font-style: italic;
  }

  .tag {
    font-size: 0.75rem;
    padding: 0.1rem 0.4rem;
    border-radius: 0.25rem;
    background: var(--color-zinc-700);
    color: var(--color-zinc-200);
    white-space: nowrap;
  }

  .id {
    font-family: ui-monospace, monospace;
    font-size: 0.75rem;
    color: var(--color-zinc-400);
    word-break: break-all;
    user-select: all;
  }

  .profile {
    font-size: 0.85rem;
    color: var(--color-zinc-300);
  }

  .details {
    color: var(--color-zinc-400);
    font-size: 0.85rem;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
  }

  .toggle {
    align-self: flex-start;
    padding: 0;
    background: none;
    border: none;
    color: var(--color-zinc-300);
    font-size: 0.85rem;
    text-decoration: underline;
    cursor: pointer;
  }

  .toggle:hover {
    color: var(--color-zinc-100);
  }

  .empty-state {
    text-align: center;
    color: var(--color-zinc-400);
    padding: 1rem;
    margin: 0;
  }

  input {
    padding: 0.4rem;
    border: 1px solid var(--color-zinc-400);
    border-radius: 0.25rem;
    background-color: var(--color-zinc-200);
    color: var(--color-zinc-950);
  }

  .btn {
    padding: 0.25rem 0.75rem;
    background-color: var(--color-zinc-700);
    border-radius: 0.5rem;
    color: var(--color-zinc-200);
    border: 1px solid var(--color-zinc-600);
    font-size: 0.9rem;
    cursor: pointer;
  }

  .btn:hover {
    background-color: var(--color-zinc-600);
  }

  .btn.danger {
    background-color: var(--color-red-700);
    border-color: var(--color-red-600);
  }

  .btn.danger:hover {
    background-color: var(--color-red-600);
  }

  .btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
</style>
